import { v4 as uuidv4 } from 'uuid';

import Model from 'src/storage/list';

import { webrtc } from 'src/helpers/webrtc';

import appModel from 'src/model';

let {
  //getUserMedia,
  isWebRTCAvailable,
  makeConnection,
  getLocalStream,
  addIceCandidate,
  setAnswer
} = webrtc;

const didntRespondTimeout = 30000;

class CallModel extends Model {
  id = 'Call';
  persistent = false;
  refreshOnInit = false;
  refreshOnSubscribe = false;
  refreshConfig = {
    foreground: false
  };
  defaultState = () => ({
    current: null
  });
  apiConfig = {
    request: {
      url: 'contact/call/request',
      validation: {
        callUUID: 'string'
      },
      cancellable: true
    },
    response: {
      url: 'contact/call/response',
      validation: {
        callUUID: 'string'
      },
      cancellable: true
    },
    icecandidates: {
      url: 'contact/call/icecandidates',
      validation: {
        callUUID: 'string'
      },
      cancellable: true
    },
    end: {
      url: 'contact/call/end',
      validation: {
        callUUID: 'string'
      },
      cancellable: true
    },
    getInfo: {
      url: 'contact/call/getInfo',
      validation: {
        callUUID: 'string'
      },
      cancellable: true
    },
    extraparams: {
      url: 'contact/call/extraparams',
      validation: {
        callUUID: 'string'
      },
      cancellable: false
    }
  };
  _events = {
    startCall: 'startCall',

    'ws:call_request': 'onCallRequest',
    'ws:call_response': 'onCallResponse',
    'ws:call_responsed': 'onCallResponsed',
    'ws:call_end': 'onCallEnd',
    'ws:call_icecandidates': 'onCallIcecandidates',
    'ws:call_extraparams': 'onCallExtraParams'
  };

  // triggered when user click call button
  startCall = (data) => {
    this.log('startCall');
    if (!data?.partner?.id) return this.warn('startCall err - no partner id');
    if (this.isBusy()) return this.warn('startCall: busy');
    let { partner, video } = data;
    video = !!video;
    let partnerId = partner.id;
    let supported = isWebRTCAvailable();
    const callUUID = uuidv4().toLowerCase();
    let calleeName = partner.firstName + ' ' + partner.lastName;
    if (partner.isDoctor) calleeName = 'Dr. ' + calleeName;
    const calleeEmail = partner.email || 'unknown@email.com';
    this.log('startCall', calleeName, callUUID);
    let showPermissionsMessageTimeout = setTimeout(() => {
      if (this.isCallUuidChanged(callUUID, 'showPermissionsMessageTimeout')) return;
      this.setListState({ showPermissionsMessage: true }, callUUID);
    }, 5000);
    this.setListStateSilent(
      {
        callUUID,
        state: 'initialization',
        callerId: this.userId,
        calleeId: partnerId,
        calleeName,
        calleeEmail,
        isCaller: true,
        isCallee: false,
        disconnected: false,
        error: supported ? false : 'webrtc not supported',

        partnerName: calleeName,
        partnerAvatar: partner.avatar,
        partnerMajor: partner.major || '',

        partnerId,
        partnerFirstName: partner.firstName,
        partnerLastName: partner.lastName,
        partner: partner,
        video,
        supported,
        requestTimeout: null,
        requestTimeouted: false,
        showPermissionsMessage: false,
        showPermissionsMessageTimeout,
        localStream: null,
        localStreamError: null,
        connectionError: null,
        remoteStream: null,
        audioEnabled: true,
        videoEnabled: video,
        remoteVideoMuted: false,
        icecandidates: [],
        startDate: 0,
        endDate: 0
      },
      callUUID
    );
    this.setState({ current: callUUID });
    if (!supported) return this.warn('startCall: webrtc not supported by browser');
    getLocalStream(video, (localStreamError, localStream) => {
      if (this.isCallUuidChanged(callUUID, 'getLocalStream')) return;
      if (localStreamError) {
        this.setListState({ localStreamError, error: 'localStreamError' }, callUUID);
        this.endCall({ callUUID });
        return;
      }
      let requestTimeout = setTimeout(() => {
        let { state } = this.getListState(callUUID);
        if (state === 'requested') {
          this.setListState(
            { state: 'requestTimeouted', requestTimeouted: true, error: 'requestTimeouted' },
            callUUID
          );
          this.endCall({ callUUID });
        }
      }, didntRespondTimeout);
      let connection = makeConnection({
        audioRequired: true,
        videoRequired: video,
        onStream: (remoteStream) => {
          if (this.isCallUuidChanged(callUUID, 'onStream')) return;
          remoteStream.getVideoTracks().forEach((videoTrack) => {
            videoTrack.onmute = () => this.setListState({ remoteVideoMuted: true });
            videoTrack.onunmute = () => this.setListState({ remoteVideoMuted: false });
          });
          this.setListState({ remoteStream }, callUUID);
        },
        onIceCandidate: (candidate) => {
          if (this.isCallUuidChanged(callUUID, 'onIceCandidate')) return;
          let { state, response, responserId, icecandidates, error } = this.getListState(callUUID);
          icecandidates.push(candidate);
          this.setListStateSilent({ icecandidates }, callUUID);
          if (response === 'accepted' && state !== 'ended' && responserId && !error)
            this.api('icecandidates', {
              callUUID,
              partnerId: responserId,
              icecandidates: [candidate]
            });
        },
        onDisconnect: (event) => {
          if (this.isCallUuidChanged(callUUID, 'onDisconnect')) return;
          this.setListStateSilent(
            { disconnected: event || true, error: 'disconnected', endDate: Date.now() },
            callUUID
          );
          this.endCall({ callUUID });
        },
        localStream,
        createOffer: (err, webrtcOffer) => {
          if (this.isCallUuidChanged(callUUID, 'createOffer')) return;
          if (err || !webrtcOffer) {
            this.warn('createOffer err', err || '!webrtcOffer');
            this.setListState(
              { connectionError: err || '!webrtcOffer', error: 'connectionError' },
              callUUID
            );
            return;
          }
          this.setListStateSilent({ state: 'requested', webrtcOffer }, callUUID);
          this.emit('callSound', true);
          this.api('request', { callUUID, video, webrtcOffer, partnerId });
        }
      });
      this.setListState({ requestTimeout, localStream, connection }, callUUID);
    });
  };

  // triggered when someone calling via websocket
  onCallRequest = (params) => {
    let {
      callUUID,
      callerId,
      calleeId,
      callerName,
      callerAvatar,
      callerMajor,
      callerEmail,
      video,
      webrtcOffer
    } = params;
    if (!(callUUID && callerId && callerName)) {
      return this.warn('onCallRequest: invalid request', params);
    } else {
      this.warn('onCallRequest', callUUID);
    }
    if (this.isBusy()) {
      if (this.state.current === callUUID) {
        return this.warn('onCallRequest: already got call request, ignoring');
      } else {
        this.api('end', { callUUID, reason: 'busy' });
        return this.warn('onCallRequest: busy');
      }
    }
    this.setListStateSilent(
      {
        state: 'requested',
        callUUID,
        callerId,
        calleeId,
        callerEmail,

        partnerName: callerName,
        partnerAvatar: callerAvatar,
        partnerMajor: callerMajor,

        isCaller: false,
        isCallee: true,
        video,
        supported: isWebRTCAvailable(),
        webrtcOffer,
        localStream: null,
        localStreamError: null,
        connectionError: null,
        remoteStream: null,
        audioEnabled: true,
        videoEnabled: video,
        remoteVideoMuted: false,
        icecandidates: [],
        startDate: 0,
        endDate: 0
      },
      callUUID
    );
    this.setState({ current: callUUID });
    this.emit('callSound', true);
  };

  loadWebrtcOfferIfNeeded = (callUUID, cb) => {
    let { webrtcOffer } = this.getListState(callUUID);
    if (webrtcOffer) return cb(webrtcOffer);
    this.api('getInfo', { callUUID }, (error, res) => {
      if (this.isCallUuidChanged(callUUID, 'loadWebrtcOfferIfNeeded')) return cb(null);
      if (error || !res) return cb(null);
      let webrtcOffer = res && res.webrtcOffer;
      if (webrtcOffer) this.setListStateSilent({ webrtcOffer }, callUUID);
      cb(webrtcOffer);
    });
  };

  switchAccount = async (calleeId, callerId) => {
    if (appModel.state.user.id == calleeId) return;
    return new Promise((resolve, reject) => {
      appModel.switchAccount({ id: calleeId, partnerId: callerId }, (error, res, success) => {
        success ? resolve() : reject(error || res);
      });
    });
  };

  // triggered when user click 'accept' button
  answerCall = async ({ callUUID }) => {
    this.warn('answerCall', callUUID);
    this.emit('callSound', false);
    let { video, response, callerId, calleeId } = this.getListState(callUUID);
    if (response) return this.warn('acceptCall: return, already responsed');
    this.setListState({ state: 'responsed', response: 'accepted' }, callUUID);
    if (appModel.state.user.id != calleeId) {
      await this.switchAccount(calleeId, callerId);
    }
    this.loadWebrtcOfferIfNeeded(callUUID, (webrtcOffer) => {
      if (!webrtcOffer) {
        this.warn('!webrtcOffer', webrtcOffer);
        this.setListStateSilent({ connectionError: '!token', error: 'connectionError' }, callUUID);
        this.endCall({ callUUID });
      }
      getLocalStream(video, (localStreamError, localStream) => {
        if (this.isCallUuidChanged(callUUID, 'getLocalStream')) return;
        if (localStreamError) {
          this.warn('acceptCall - localStreamError', localStreamError);
          this.setListState({ localStreamError, error: 'localStreamError' }, callUUID);
          this.endCall({ callUUID });
        }
        let connection = makeConnection({
          audioRequired: true,
          videoRequired: video,
          onStream: (remoteStream) => {
            if (this.isCallUuidChanged(callUUID, 'onStream')) return;
            remoteStream.getVideoTracks().forEach((videoTrack) => {
              videoTrack.onmute = () => this.setListState({ remoteVideoMuted: true });
              videoTrack.onunmute = () => this.setListState({ remoteVideoMuted: false });
            });
            this.setListState({ remoteStream }, callUUID);
          },
          onIceCandidate: (candidate) => {
            if (this.isCallUuidChanged(callUUID, 'onIceCandidate')) return;
            let { icecandidates, callerId, state, error } = this.getListState(callUUID);
            icecandidates.push(candidate);
            this.setListStateSilent({ icecandidates }, callUUID);
            if (state !== 'ended' && !error) {
              this.api('icecandidates', {
                callUUID,
                partnerId: callerId,
                icecandidates: [candidate]
              });
            }
          },
          onDisconnect: (event) => {
            if (this.isCallUuidChanged(callUUID, 'onDisconnect')) return;
            this.setListStateSilent(
              { disconnected: event || true, error: 'disconnected', endDate: Date.now() },
              callUUID
            );
            this.endCall({ callUUID });
          },
          localStream,
          offer: webrtcOffer,
          createAnswer: (connectionError, webrtcAnswer) => {
            if (this.isCallUuidChanged(callUUID, 'createAnswer')) return;
            if (connectionError || !webrtcAnswer) {
              this.warn('createAnswer err', connectionError, webrtcOffer);
              this.setListState({ connectionError, error: 'connectionError' });
              return this.endCall({ callUUID });
            }
            this.setListState({ startDate: Date.now() }, callUUID);
            this.api('response', {
              callUUID,
              response: 'accepted',
              webrtcAnswer
            });
          }
        });
        this.setListState({ localStream, connection }, callUUID);
      });
    });
  };

  // triggered when received endCall event,
  // via hangup button or onCallResponsed websocket event or endCall websocket event or at any error
  endCall = ({ callUUID }) => {
    this.log('endCall', callUUID);
    this.emit('callSound', false);
    let {
      state,
      endReason,
      error,
      connection,
      localStream,
      requestTimeout,
      showPermissionsMessageTimeout
    } = this.getListState(callUUID);
    if (!endReason) endReason = error || 'hangup';
    if (state !== 'ended') {
      this.setListState({ state: 'ended', endReason, endDate: Date.now() });
      this.api('end', { callUUID, reason: endReason }, () => {
        try {
          if (connection) connection.close();
          if (localStream) localStream.getTracks().forEach((track) => track.stop());
        } catch (err) {
          this.warn('endCall err', callUUID, err);
        }
      });
    } else {
      try {
        if (connection) connection.close();
        if (localStream) localStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        this.warn('endCall err', callUUID, err);
      }
    }
    clearTimeout(requestTimeout);
    clearTimeout(showPermissionsMessageTimeout);
    if (this.state.current === callUUID) {
      if (endReason === 'hangup' || endReason === 'responsedFromOtherDevice')
        this.setState({ current: null });
    }
  };

  // triggered when caller receive response to his call request via websocket
  onCallResponse = (params) => {
    const { callUUID, response, webrtcAnswer, responserId } = params;
    if (!callUUID || !response || !webrtcAnswer)
      return this.warn('onCallResponse fail: invalid response', params);
    this.log('onCallResponse', params);
    if (this.isCallUuidChanged(callUUID, 'onCallResponse')) return;
    this.emit('callSound', false);
    let { state, connection, icecandidates } = this.getListState(callUUID);
    if (state !== 'requested') return this.warn('onCallResponse fail: not in requested state');
    if (!connection) return this.warn('onCallResponse fail: no connection');
    setAnswer(connection, webrtcAnswer);
    if (icecandidates && icecandidates.length > 0) {
      this.api('icecandidates', { callUUID, partnerId: responserId, icecandidates });
    }
    this.setListState(
      {
        state: 'responsed',
        response,
        responserId,
        webrtcAnswer,
        startDate: response === 'accepted' ? Date.now() : 0
      },
      callUUID
    );
  };

  // triggered when you sent response from any of your devices
  // if responsed from other device, close call request
  // delivered via websocket
  onCallResponsed = ({ callUUID }) => {
    this.log('onCallResponsed', callUUID);
    if (this.isCallUuidChanged(callUUID, 'onCallResponsed')) return;
    let { response } = this.getListState(callUUID);
    if (!response) {
      this.setListStateSilent(
        { state: 'ended', endReason: 'responsedFromOtherDevice', endDate: Date.now() },
        callUUID
      );
      this.endCall({ callUUID });
    }
  };

  onCallIcecandidates = ({ callUUID, partnerId, icecandidates }) => {
    // this.log('onCallIcecandidates', callUUID, partnerId, icecandidates);
    if (this.isCallUuidChanged(callUUID, 'onCallIcecandidates')) return;
    let { connection } = this.getListState(callUUID);
    if (connection && icecandidates && icecandidates.length > 0) {
      icecandidates.forEach((icecandidate) => {
        addIceCandidate(connection, icecandidate);
      });
    } else {
      this.warn('onCallIcecandidates invalid', partnerId, icecandidates);
    }
  };

  // triggered when received extraParams event via websocket - for example remoteVideoMuted
  onCallExtraParams = ({ callUUID, extraParams }) => {
    this.log('onCallExtraParams', callUUID, extraParams);
    if (this.isCallUuidChanged(callUUID, 'onCallExtraParams')) return;
    let newParams = {};
    Object.keys(extraParams || {}).forEach((key) => {
      if (['remoteVideoMuted'].includes(key)) {
        newParams[key] = extraParams[key];
      }
    });
    this.setListStateSilent({ ...newParams }, callUUID);
  };

  // triggered when received endCall event via websocket
  onCallEnd = ({ callUUID, reason }) => {
    this.log('onCallEnd', callUUID, reason);
    if (this.isCallUuidChanged(callUUID, 'onCallEnd')) return;
    let { isCaller, state } = this.getListState(callUUID);
    if (isCaller && state === 'requested' && reason === 'hangup') reason = 'rejected';
    this.setListState({ state: 'ended', endReason: reason, endDate: Date.now() }, callUUID);
    this.endCall({ callUUID });
  };

  toggleVideo = () => {
    let callUUID = this.state.current;
    let { localStream, videoEnabled } = this.getListState(callUUID);
    if (!callUUID || !localStream) {
      return this.warn('toggleVideo fail');
    }
    videoEnabled = !videoEnabled;
    this.setListState({ videoEnabled }, callUUID);
    let tracks = localStream.getVideoTracks();
    if (tracks && tracks[0]) {
      tracks[0].enabled = videoEnabled;
    } else if (videoEnabled) {
      getLocalStream(
        true,
        (localStreamError, newLocalStream) => {
          if (this.state.current === callUUID) {
            this.setListState(
              {
                localStream: new MediaStream([
                  ...localStream.getTracks(),
                  ...newLocalStream.getTracks()
                ]),
                videoEnabled
              },
              callUUID
            );
          } else {
            newLocalStream.getTracks().forEach((track) => track.stop());
          }
        },
        false
      );
    }
  };

  toggleAudio = () => {
    let callUUID = this.state.current;
    let { localStream, audioEnabled } = this.getListState(callUUID);
    if (!callUUID || !localStream) {
      return this.warn('toggleVideo fail');
    }
    audioEnabled = !audioEnabled;
    this.setListState({ audioEnabled }, callUUID);
    let tracks = localStream.getAudioTracks();
    if (tracks && tracks[0]) {
      tracks[0].enabled = audioEnabled;
    }
  };

  isCallUuidChanged = (callUUID, event) => {
    let changed = this.state.current !== callUUID;
    if (changed) this.warn('callUUID changed', event, callUUID, this.state.current);
    return changed;
  };

  // is user already in call with someone
  isBusy = () => {
    let callUUID = this.state.current;
    if (!callUUID) {
      return false;
    } else {
      let { state, error } = this.getListState(callUUID);
      if (state == 'ended' || error) return false;
      return true;
    }
  };
}

const model = new CallModel();
export default model;
